import { useEffect, useState } from 'react';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  useServiceRequestDashboardApi,
  UseServiceRequestDashboardApiReturn,
} from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import {
  BulkUpdateVisibilityResult,
  useServiceRequestVisibilityApi,
  UseServiceRequestVisibilityApiReturn,
} from 'src/services/api/ServiceRequestDashboard/useServiceRequestVisibilityApi';
import { selectConfirmedChanges } from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityFormState/helpers';
import useProperties, {
  UsePropertiesReturn,
} from 'src/utils/CommonDataFetchingUtils/useProperties';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';
import {
  ProjectFilter,
  StepId,
  UseServiceRequestVisibilityFormStateReturn,
  WIZARD_STEP_CONFIRMATION,
  useServiceRequestVisibilityFormState,
} from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityFormState/useServiceRequestVisibilityFormState';

const EMPTY_RESULT: BulkUpdateVisibilityResult = {
  ok: false,
  updatedCount: 0,
  updatedRecords: [],
};

export interface UseServiceRequestVisibilityOrchestratorArgs {
  isOpen: boolean;
  onSuccess?: (result: BulkUpdateVisibilityResult) => void;
}

export interface UseServiceRequestVisibilityOrchestratorReturn {
  currentStep: StepId;
  projectFilter: ProjectFilter;
  searchQuery: string;
  selectedIds: Set<string>;

  setCurrentStep: (step: StepId) => void;
  setProjectFilter: (projectId: ProjectFilter) => void;
  setSearchQuery: (query: string) => void;

  toggleSelection: (id: string) => void;
  setSelectAll: (ids: string[], selected: boolean) => void;
  clearSelection: () => void;

  targetIsInternalOnly: boolean;
  setTargetIsInternalOnly: (isInternalOnly: boolean) => void;

  availableProjects: IProperty[];
  serviceRequests: IServiceRequest[];
  filteredServiceRequests: IServiceRequest[];
  stagedServiceRequests: IServiceRequest[];
  actualChanges: IServiceRequest[];
  unchangedServiceRequests: IServiceRequest[];
  confirmationChangedServiceRequests: IServiceRequest[];
  willChangeIds: string[];
  willBecomeInternalCount: number;
  willBecomeExternalCount: number;

  isLoadingServiceRequests: boolean;
  serviceRequestsError: string | null;
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
  lastApplyResult: BulkUpdateVisibilityResult | null;

  canContinueFromStep1: boolean;
  canApplyFromStep2: boolean;

  stageSelectedAndContinue: () => void;
  goToStep: (step: StepId) => void;
  handleApply: () => Promise<BulkUpdateVisibilityResult>;
  handleStartOver: () => void;
  reset: () => void;
}

export const useServiceRequestVisibilityOrchestrator = ({
  isOpen,
  onSuccess,
}: UseServiceRequestVisibilityOrchestratorArgs): UseServiceRequestVisibilityOrchestratorReturn => {
  const dashboardApi: UseServiceRequestDashboardApiReturn =
    useServiceRequestDashboardApi();

  const visibilityApi: UseServiceRequestVisibilityApiReturn =
    useServiceRequestVisibilityApi();

  const { data: properties, fetchProperties }: UsePropertiesReturn =
    useProperties();

  const formState: UseServiceRequestVisibilityFormStateReturn =
    useServiceRequestVisibilityFormState({
      serviceRequests: dashboardApi.serviceRequests,
    });

  const [lastApplyResult, setLastApplyResult] =
    useState<BulkUpdateVisibilityResult | null>(null);

  const [
    confirmationChangedServiceRequests,
    setConfirmationChangedServiceRequests,
  ] = useState<IServiceRequest[]>([]);

  useEffect(() => {
    void fetchProperties();
  }, []);

  useEffect(() => {
    if (isOpen) {
      void dashboardApi.fetchDashboard();
    }
  }, [isOpen]);

  const availableProjects: IProperty[] = properties ?? [];

  const handleApply = async (): Promise<BulkUpdateVisibilityResult> => {
    if (!formState.canApplyFromStep2) {
      return EMPTY_RESULT;
    }

    // legacy/workaround: BE speaks "external visibility", the inverse of our
    // internal-only model — flip at the boundary so the FE stays consistent.
    const externalVisibilityOverride: boolean = !formState.targetIsInternalOnly;

    const result: BulkUpdateVisibilityResult =
      await visibilityApi.bulkUpdateVisibility(
        formState.willChangeIds,
        externalVisibilityOverride
      );

    if (result.ok) {
      const confirmedChanges: IServiceRequest[] = selectConfirmedChanges(
        result.updatedRecords,
        formState.actualChanges
      );

      setLastApplyResult(result);
      setConfirmationChangedServiceRequests(confirmedChanges);
      formState.setCurrentStep(WIZARD_STEP_CONFIRMATION);
      onSuccess?.(result);
    }

    return result;
  };

  const handleStartOver = (): void => {
    formState.reset();
    visibilityApi.clearError();
    visibilityApi.clearSuccess();
    setLastApplyResult(null);
    setConfirmationChangedServiceRequests([]);
  };

  const reset = (): void => {
    formState.reset();
    visibilityApi.clearError();
    visibilityApi.clearSuccess();
    setLastApplyResult(null);
    setConfirmationChangedServiceRequests([]);
  };

  return {
    currentStep: formState.currentStep,
    projectFilter: formState.projectFilter,
    searchQuery: formState.searchQuery,
    selectedIds: formState.selectedIds,

    setCurrentStep: formState.setCurrentStep,
    setProjectFilter: formState.setProjectFilter,
    setSearchQuery: formState.setSearchQuery,

    toggleSelection: formState.toggleSelection,
    setSelectAll: formState.setSelectAll,
    clearSelection: formState.clearSelection,

    targetIsInternalOnly: formState.targetIsInternalOnly,
    setTargetIsInternalOnly: formState.setTargetIsInternalOnly,

    availableProjects,
    serviceRequests: dashboardApi.serviceRequests,
    filteredServiceRequests: formState.filteredServiceRequests,
    stagedServiceRequests: formState.stagedServiceRequests,
    actualChanges: formState.actualChanges,
    unchangedServiceRequests: formState.unchangedServiceRequests,
    confirmationChangedServiceRequests,
    willChangeIds: formState.willChangeIds,
    willBecomeInternalCount: formState.willBecomeInternalCount,
    willBecomeExternalCount: formState.willBecomeExternalCount,

    isLoadingServiceRequests: dashboardApi.listLoading,
    serviceRequestsError: null,
    isSubmitting: visibilityApi.isSubmitting,
    error: visibilityApi.error,
    successMessage: visibilityApi.successMessage,
    lastApplyResult,

    canContinueFromStep1: formState.canContinueFromStep1,
    canApplyFromStep2: formState.canApplyFromStep2,

    stageSelectedAndContinue: formState.stageSelectedAndContinue,
    goToStep: formState.goToStep,
    handleApply,
    handleStartOver,
    reset,
  };
};